import { useMemo, useRef, useState } from 'react';
import { Box, Button, Group, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconCopy,
  IconDownload,
  IconFileTypeCsv,
  IconPhoto,
} from '@tabler/icons-react';
import {
  canCopyImage,
  copyPng,
  downloadCsv,
  downloadPng,
  downloadSvg,
  filenameFor,
} from '../utils/chartExport';

/**
 * Renders the `visualization` payload the RAG service returns.
 *
 * The service decides whether a chart is appropriate and what shape it takes;
 * this only draws what it is given. Two of its rules matter when reading the
 * output:
 *
 *   - no chart for fewer than two rows, because a one-bar chart shows a single
 *     number the answer text already states;
 *   - no chart without a dimension, because there is nothing to plot across.
 *
 * So a missing chart is usually correct, not a failure.
 *
 * Contract: { type, title, categoryLabel, currency, defaultVisibleRows,
 *             series: [{ key, label, format }], data: [{ label, value, ... }] }
 */

// Categorical series colours, assigned in fixed order and never cycled.
//
// Leads with the brand teal so a single-series chart — the common case — is on
// brand rather than orange, which belonged to no palette in this app.
//
// The order is not cosmetic. Validated against the six colour checks in both
// light and dark mode; the previous palette failed two of them:
//
//   - '#0b7285' sat below the chroma floor, reading as grey rather than a hue
//   - '#d9480f' and '#5c940d' were adjacent at deuteranopia dE 1.6, so a
//     red-green colourblind reader could not tell series 7 from series 8
//
// Reds and greens are now separated by blues, and the purple and maroon were
// lightened so every entry stays inside the lightness band against the dark
// surface too. Worst adjacent pair is now dE 14.2 (deutan).
//
// Re-validate before reordering or adding an entry — the failures above are
// invisible to anyone with normal colour vision.
const PALETTE = [
  '#039DC8', // brand teal
  '#c92a2a', // red
  '#1864ab', // blue
  '#2b8a3e', // green
  '#9c36b5', // purple
  '#e8590c', // orange
  '#364fc7', // indigo
  '#5c940d', // olive
  '#c2255c', // pink
  '#0ca678', // teal
];

/** Mirrors the service's own formatting so the chart and the answer text agree. */
function formatValue(value, series, currency) {
  if (series.format === 'currency') {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency || 'ZAR',
      maximumFractionDigits: 2,
    }).format(value || 0);
  }
  if (series.format === 'percentage') {
    return `${new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 }).format(value || 0)}%`;
  }
  return new Intl.NumberFormat('en-ZA', {
    maximumFractionDigits: series.format === 'integer' ? 0 : 2,
  }).format(value || 0);
}

function shortenLabel(value, maxLength = 26) {
  const text = String(value ?? 'Unknown');
  return text.length <= maxLength ? text : `${text.slice(0, maxLength - 3)}...`;
}

function slicePath(cx, cy, radius, startAngle, endAngle) {
  const point = (angle) => [
    cx + radius * Math.cos(angle),
    cy + radius * Math.sin(angle),
  ];
  const [x1, y1] = point(startAngle);
  const [x2, y2] = point(endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

function BarChart({ data, series, currency }) {
  const width = 760;
  const rowHeight = 34;
  const left = 190;
  const right = 150;
  const top = 12;
  const plotWidth = width - left - right;
  const height = top + data.length * rowHeight + 12;
  const maximum = Math.max(
    ...data.map((row) => Number(row[series.key]) || 0),
    1,
  );

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      role="img"
      aria-label={series.label}>
      {data.map((row, index) => {
        const value = Number(row[series.key]) || 0;
        const barWidth = Math.max(1, (value / maximum) * plotWidth);
        const y = top + index * rowHeight;
        return (
          <g key={`${row.label}-${index}`}>
            <text
              x={left - 10}
              y={y + 20}
              textAnchor="end"
              fontSize="12"
              fill="#2b2f33">
              <title>{row.label}</title>
              {shortenLabel(row.label)}
            </text>
            <rect
              x={left}
              y={y + 7}
              width={barWidth}
              height={rowHeight - 16}
              rx="3"
              fill={PALETTE[index % PALETTE.length]}
            />
            <text
              x={left + barWidth + 8}
              y={y + 20}
              fontSize="12"
              fill="#2b2f33">
              {formatValue(value, series, currency)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// The legend is drawn INSIDE the svg, not as HTML beside it.
//
// Export serialises the <svg> element alone (see chartExport.js), so an HTML
// legend is simply absent from the downloaded PNG and SVG — the pie arrived as
// unlabelled coloured wedges while the on-screen version looked complete. Bar
// and line charts never had the problem because their labels are already <text>
// nodes. One drawing means the download cannot drift from the screen.
function PieChart({ data, series, currency }) {
  const pie = 300;
  const padding = 8;
  const rowHeight = 22;
  const swatch = 10;
  const legendX = pie + 8;
  const labelX = legendX + swatch + 8;
  const valueRight = labelX + 300;
  const width = valueRight + padding;
  const height = Math.max(pie, data.length * rowHeight + padding * 2);
  const centre = pie / 2;
  const radius = centre - padding;
  const total = data.reduce(
    (sum, row) => sum + (Number(row[series.key]) || 0),
    0,
  );

  let angle = -Math.PI / 2;
  const slices = data.map((row, index) => {
    const value = Number(row[series.key]) || 0;
    const sweep = total > 0 ? (value / total) * Math.PI * 2 : 0;
    const path = slicePath(centre, height / 2, radius, angle, angle + sweep);
    angle += sweep;
    return { row, value, path, colour: PALETTE[index % PALETTE.length] };
  });

  const legendTop = (height - data.length * rowHeight) / 2 + rowHeight / 2;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      role="img"
      aria-label={series.label}
      // Named explicitly: a serialised SVG opened outside the app has no
      // stylesheet to inherit a font from.
      fontFamily="system-ui, -apple-system, Segoe UI, Roboto, sans-serif">
      {slices.map((slice, index) => (
        <path
          key={index}
          d={slice.path}
          fill={slice.colour}
          stroke="#fff"
          strokeWidth="1"
        />
      ))}
      {slices.map((slice, index) => {
        const y = legendTop + index * rowHeight;
        return (
          <g key={`legend-${index}`}>
            <title>{slice.row.label}</title>
            <rect
              x={legendX}
              y={y - swatch / 2}
              width={swatch}
              height={swatch}
              rx="2"
              fill={slice.colour}
            />
            <text x={labelX} y={y + 4} fontSize="12" fill="#2b2f33">
              {shortenLabel(slice.row.label, 26)}
            </text>
            <text
              x={valueRight}
              y={y + 4}
              fontSize="12"
              fontWeight="600"
              fill="#2b2f33"
              textAnchor="end">
              {formatValue(slice.value, series, currency)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ data, series, currency }) {
  const width = 760;
  const height = 280;
  const left = 70;
  const right = 20;
  const top = 14;
  const bottom = 58;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const values = data.map((row) => Number(row[series.key]) || 0);
  const maximum = Math.max(...values, 1);
  const step = data.length > 1 ? plotWidth / (data.length - 1) : 0;
  const points = values.map((value, index) => [
    left + index * step,
    top + plotHeight - (value / maximum) * plotHeight,
  ]);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      role="img"
      aria-label={series.label}>
      <line
        x1={left}
        y1={top + plotHeight}
        x2={left + plotWidth}
        y2={top + plotHeight}
        stroke="#ced4da"
      />
      <polyline
        fill="none"
        stroke={PALETTE[0]}
        strokeWidth="2"
        points={points.map(([x, y]) => `${x},${y}`).join(' ')}
      />
      {points.map(([x, y], index) => (
        <g key={index}>
          <circle cx={x} cy={y} r="3.5" fill={PALETTE[0]} />
          <text
            x={x}
            y={top + plotHeight + 18}
            fontSize="11"
            fill="#495057"
            textAnchor="end"
            transform={`rotate(-35 ${x} ${top + plotHeight + 18})`}>
            {shortenLabel(data[index].label, 16)}
          </text>
        </g>
      ))}
      <text
        x={left - 8}
        y={top + 6}
        fontSize="11"
        fill="#868e96"
        textAnchor="end">
        {formatValue(maximum, series, currency)}
      </text>
    </svg>
  );
}

/**
 * Copy / download controls for a rendered chart.
 *
 * Deliberately below the chart rather than floating over it: these are
 * occasional actions, and an overlay would cover data in a panel this narrow.
 *
 * Every action reports its outcome in place — a copy that silently does
 * nothing is indistinguishable from one that worked, and clipboard writes fail
 * for ordinary reasons (permission, an unfocused document, Firefox having no
 * ClipboardItem at all).
 */
function ChartActions({ holder, visualization }) {
  const [status, setStatus] = useState(null);

  const run = async (label, action) => {
    try {
      const svg = holder.current?.querySelector('svg');
      if (!svg) throw new Error('The chart is not ready yet');
      await action(svg);
      setStatus({ ok: true, message: `${label} done` });
    } catch (cause) {
      setStatus({ ok: false, message: cause?.message || `${label} failed` });
    }
    setTimeout(() => setStatus(null), 2500);
  };

  const name = filenameFor(visualization);

  return (
    <Stack spacing={4} mt="xs">
      <Group spacing="xs">
        {canCopyImage ? (
          <Button
            size="compact-xs"
            variant="light"
            color="cyan"
            leftSection={<IconCopy size={13} />}
            onClick={() => run('Copy graph', (svg) => copyPng(svg))}>
            Copy graph
          </Button>
        ) : (
          <Tooltip
            label="This browser cannot put images on the clipboard — use Download PNG"
            withArrow>
            <Button
              size="compact-xs"
              variant="light"
              color="gray"
              disabled
              leftSection={<IconCopy size={13} />}>
              Copy graph
            </Button>
          </Tooltip>
        )}
        <Button
          size="compact-xs"
          variant="light"
          color="cyan"
          leftSection={<IconPhoto size={13} />}
          onClick={() => run('Download PNG', (svg) => downloadPng(svg, name))}>
          Download PNG
        </Button>
        <Button
          size="compact-xs"
          variant="light"
          color="cyan"
          leftSection={<IconDownload size={13} />}
          onClick={() => run('Download SVG', (svg) => downloadSvg(svg, name))}>
          Download SVG
        </Button>
        <Button
          size="compact-xs"
          variant="light"
          color="cyan"
          leftSection={<IconFileTypeCsv size={13} />}
          onClick={() =>
            run('Download CSV', () => downloadCsv(visualization, name))
          }>
          Download CSV
        </Button>
      </Group>
      {status ? (
        <Text size="xs" c={status.ok ? 'cyan.8' : 'red.7'}>
          {status.message}
        </Text>
      ) : null}
    </Stack>
  );
}

function JuiceChart({ visualization }) {
  const holder = useRef(null);
  const data = useMemo(() => {
    if (!visualization?.data?.length) return [];
    const visible = Math.min(
      visualization.defaultVisibleRows || 20,
      visualization.data.length,
    );
    return visualization.data.slice(0, visible);
  }, [visualization]);

  const series = visualization?.series?.[0];
  if (!series || !data.length) return null;

  const { type, title, currency } = visualization;
  let Chart = BarChart;
  if (type === 'pie') Chart = PieChart;
  else if (type === 'line') Chart = LineChart;

  return (
    <Box mt="sm">
      {title ? (
        <Text size="sm" fw={600} mb={6}>
          {title}
        </Text>
      ) : null}
      {/* The export helpers read the live <svg>, so the ref goes on a wrapper
          rather than being threaded through all three chart components. */}
      <Box ref={holder}>
        <Chart data={data} series={series} currency={currency} />
      </Box>
      {visualization.data.length > data.length ? (
        <Text size="xs" c="dimmed" mt={6}>
          Showing the top {data.length} of {visualization.data.length}. Exports
          include all {visualization.data.length}.
        </Text>
      ) : null}
      <ChartActions holder={holder} visualization={visualization} />
    </Box>
  );
}

export default JuiceChart;
